if (customElements.get('seen-on-you') === undefined) {
  class SeenOnYou extends HTMLElement {
    constructor() {
      super();
      this.currentIdx = 0;
      this.scrollY = 0;

      this.onCardClick = this.onCardClick.bind(this);
      this.onLightboxClick = this.onLightboxClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.close = this.close.bind(this);
      this.onVideoEnded = this.onVideoEnded.bind(this);
      this.onVideoTimeupdate = this.onVideoTimeupdate.bind(this);
    }

    connectedCallback() {
      this.lightbox = this.querySelector('[data-seen-lightbox]');
      if (!this.lightbox) return;

      this.frameVideo = this.lightbox.querySelector('[data-seen-lightbox-video]');
      this.closeBtn = this.lightbox.querySelector('[data-seen-lightbox-close]');
      this.progressBar = this.lightbox.querySelector('[data-seen-lightbox-progress]');
      this.dots = this.lightbox.querySelectorAll('[data-seen-lightbox-dot]');
      this.slides = Array.from(this.querySelectorAll('[data-seen-card][data-video-src]'));

      if (this.slides.length === 0) return;

      this.slides.forEach((card) => card.addEventListener('click', this.onCardClick));
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.close);
      this.lightbox.addEventListener('click', this.onLightboxClick);
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.setSlide(i));
      });
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.slides) this.slides.forEach((card) => card.removeEventListener('click', this.onCardClick));
      if (this.closeBtn) this.closeBtn.removeEventListener('click', this.close);
      if (this.lightbox) this.lightbox.removeEventListener('click', this.onLightboxClick);
      if (this.frameVideo) {
        this.frameVideo.removeEventListener('ended', this.onVideoEnded);
        this.frameVideo.removeEventListener('timeupdate', this.onVideoTimeupdate);
      }

      // If the lightbox was open when we disconnect (theme-editor block reorder,
      // Section Rendering re-render), restore the body scroll-lock styles so
      // the page doesn't stay frozen at position: fixed with no way to close.
      if (this.lightbox && this.lightbox.classList.contains('is-open')) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo({ top: this.scrollY, left: 0, behavior: 'instant' });
      }
    }

    onCardClick(e) {
      const card = e.currentTarget;
      const idx = parseInt(card.dataset.seenIdx, 10) || 0;
      this.open(idx);
    }

    onLightboxClick(e) {
      if (e.target === this.lightbox) this.close();
    }

    onKeydown(e) {
      if (!this.lightbox || !this.lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') this.close();
      else if (e.key === 'ArrowRight' && this.currentIdx + 1 < this.slides.length) this.setSlide(this.currentIdx + 1);
      else if (e.key === 'ArrowLeft' && this.currentIdx > 0) this.setSlide(this.currentIdx - 1);
    }

    onVideoEnded() {
      // Advance when the actual video finishes (real duration, no fixed timer).
      if (this.currentIdx + 1 >= this.slides.length) this.close();
      else this.setSlide(this.currentIdx + 1);
    }

    onVideoTimeupdate() {
      if (!this.frameVideo || !this.progressBar) return;
      const dur = this.frameVideo.duration;
      if (!isFinite(dur) || dur <= 0) return;
      const pct = Math.min(100, (this.frameVideo.currentTime / dur) * 100);
      this.progressBar.style.width = pct + '%';
    }

    setSlide(idx) {
      this.currentIdx = idx;
      const src = this.slides[idx].dataset.videoSrc;
      const type = this.slides[idx].dataset.videoType || 'video/mp4';

      if (this.frameVideo) {
        // Re-wire listeners cleanly for the new slide. Using add/removeEventListener
        // (not on* properties) so disconnectedCallback can remove them by reference.
        this.frameVideo.removeEventListener('ended', this.onVideoEnded);
        this.frameVideo.removeEventListener('timeupdate', this.onVideoTimeupdate);

        this.frameVideo.innerHTML = '<source src="' + src + '" type="' + type + '">';
        this.frameVideo.load();

        if (this.progressBar) this.progressBar.style.width = '0%';

        this.frameVideo.addEventListener('ended', this.onVideoEnded);
        this.frameVideo.addEventListener('timeupdate', this.onVideoTimeupdate);

        const playPromise = this.frameVideo.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch((err) => {
            // Browser blocked autoplay or the source failed to load. Don't
            // throw, but surface so QA can see why a lightbox video stays
            // black (Safari Low Power Mode, missing video file, etc.).
            if (typeof console !== 'undefined') console.warn('[seen-on-you] lightbox video.play() rejected:', err && err.name, src);
          });
        }
      }

      this.dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
    }

    open(idx) {
      this.scrollY = window.scrollY;
      this.lightbox.classList.add('is-open');
      this.lightbox.removeAttribute('hidden');
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + this.scrollY + 'px';
      document.body.style.width = '100%';
      this.setSlide(idx);
    }

    close() {
      this.lightbox.classList.remove('is-open');
      this.lightbox.setAttribute('hidden', '');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      // Restore the original scroll position instantly. base.css sets
      // `scroll-behavior: smooth` globally, which would otherwise animate
      // the restore — user briefly sees the top of the page then watches
      // it smoothly scroll back to the video section.
      window.scrollTo({ top: this.scrollY, left: 0, behavior: 'instant' });
      if (this.progressBar) this.progressBar.style.width = '0%';
      if (this.frameVideo) {
        this.frameVideo.removeEventListener('ended', this.onVideoEnded);
        this.frameVideo.removeEventListener('timeupdate', this.onVideoTimeupdate);
        this.frameVideo.pause();
        this.frameVideo.removeAttribute('src');
        this.frameVideo.innerHTML = '';
        this.frameVideo.load();
      }
    }
  }

  customElements.define('seen-on-you', SeenOnYou);
}
